<template>
	<div class="planUpgrade">
		<!-- Hero Section -->
		<div class="hero">
			<div class="hero_container">
				<div class="hero_badge">
					<span class="badge_icon">✨</span>
					<span>Upgrade Disponível</span>
				</div>
				
				<h1 class="hero_title">
					Escolha o Plano <span class="highlight">Perfeito</span> para Você
				</h1>
				
				<p class="hero_subtitle">
					Recursos premium, segurança avançada e funcionalidades que fazem a diferença
				</p>

				<!-- Billing Toggle -->
				<div class="billing_toggle">
					<button 
						:class="['toggle_btn', { active: billingCycle === 'monthly' }]"
						@click="billingCycle = 'monthly'"
					>
						Mensal
					</button>
					<button 
						:class="['toggle_btn', { active: billingCycle === 'yearly' }]"
						@click="billingCycle = 'yearly'"
					>
						Anual
						<span class="discount_badge">Até -{{ getMaxDiscount() }}%</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Plans Section -->
		<div class="plans_section">
			<div class="plans_container">
				<div class="plans_grid">
					<div 
						v-for="plan in plans" 
						:key="plan.id"
						:class="['plan_card', { featured: plan.featured }]"
					>
						<div v-if="plan.featured" class="popular_badge">
							<sw-icon name="star-full" />
							<span>Mais Popular</span>
						</div>
						
						<div class="plan_content">
							<div class="plan_top">
								<div class="plan_info">
									<div :class="['plan_icon_small', plan.id]">
										<sw-icon :name="plan.icon" />
									</div>
									<div class="plan_details">
										<h3 class="plan_name">{{ plan.name }}</h3>
										<p class="plan_description">{{ plan.description }}</p>
									</div>
								</div>
								
								<div class="plan_price_compact">
									<div v-if="billingCycle === 'monthly'">
										<div class="price_value">
											<span class="currency_small">R$</span>
											<span class="amount_large">{{ plan.price.monthly.toFixed(2).replace('.', ',') }}</span>
										</div>
										<div class="price_period_small">/mês</div>
										
										<!-- Recursos Extras (sempre mensais) -->
										<div v-if="planResourcesCosts[plan.id] > 0" class="price_extras">
											+ R$ {{ planResourcesCosts[plan.id].toFixed(2).replace('.', ',') }}/mês em recursos
										</div>
									</div>
									
									<div v-else>
										<!-- Preço Anual -->
										<div class="price_value">
											<span class="currency_small">R$</span>
											<span class="amount_large">{{ getYearlyPrice(plan).toFixed(2).replace('.', ',') }}</span>
										</div>
										<div class="price_period_small">/ano</div>
										
										<!-- Recursos Extras (sempre mensais) -->
										<div v-if="planResourcesCosts[plan.id] > 0" class="price_extras">
											+ R$ {{ planResourcesCosts[plan.id].toFixed(2).replace('.', ',') }}/mês em recursos
										</div>
										
										<!-- Desconto -->
										<div class="price_savings">
											<sw-icon name="gift" />
											Economize {{ getDiscountPercentage(plan) }}%: R$ {{ getYearlySavings(plan).toFixed(2).replace('.', ',') }}
										</div>
									</div>
								</div>
							</div>

							<!-- Plan Resources Component -->
							<PlanResources
								:plan-id="plan.id"
								:initial-resources="planResources[plan.id]"
								:base-resources="getBaseResources(plan.id)"
								:storage-max="plan.id === 'essential' ? 200 : 1000"
								@update:resources="updatePlanResources(plan.id, $event)"
								@update:extra-cost="updatePlanExtraCost(plan.id, $event)"
							/>

							<div class="plan_features_detailed modal-section">
								<h4 class="features_heading">O que está incluído:</h4>
								<div class="features_list_detailed">
									<div v-for="(feature, index) in getVisibleFeatures(plan)" 
										 :key="index" 
										 :class="['feature_item_detailed', getFeatureClass(feature)]">
										<div class="feature_icon_detailed">
											<sw-icon :name="feature.included ? feature.icon : 'cross'" />
										</div>
										<div class="feature_content_detailed">
											<span class="feature_text_detailed">
												{{ feature.name }}
												<span v-if="feature.inDevelopment" class="development_badge">🚧 Em desenvolvimento</span>
											</span>
											<p v-if="feature.description" class="feature_description">
												{{ feature.description }}
											</p>
										</div>
									</div>
								</div>
								
								<!-- Botão Ver Mais/Ver Menos -->
								<div v-if="getRemainingFeaturesCount(plan) > 0 || featuresVisibility[plan.id]" class="features_toggle_container">
									<button 
										@click="toggleFeaturesVisibility(plan.id)"
										class="features_toggle_btn"
									>
										<span v-if="!featuresVisibility[plan.id]">
											Ver mais {{ getRemainingFeaturesCount(plan) }} recursos
										</span>
										<span v-else>
											Ver menos
										</span>
										<sw-icon 
											:name="featuresVisibility[plan.id] ? 'chevron-up' : 'chevron-down'" 
											class="toggle_icon" 
										/>
									</button>
								</div>
							</div>

							<button 
								:class="['plan_button_modern', { primary: plan.featured }]"
								@click="(event) => upgradeToPlan(plan.id, event)"
								type="button"
							>
								<span>{{ plan.id === 'essential' ? 'Assinar Essencial' : 'Assinar Premium' }}</span>
								<sw-icon name="arrow-right2" class="button_arrow" />
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Features Comparison -->
		<div class="features_section">
			<div class="features_container">
				<h2 class="features_title">Compare os Planos</h2>
				
				<div class="comparison_table">
					<div class="comparison_header">
						<div class="feature_name">Recursos</div>
						<div class="plan_column">Essencial</div>
						<div class="plan_column featured">Empresarial</div>
					</div>
					
					<div class="comparison_row" v-for="comparison in featureComparisons" :key="comparison.feature">
						<div class="feature_name">{{ comparison.feature }}</div>
						<div class="plan_column">
							<sw-icon :name="comparison.essential ? 'checkmark' : 'cross'" 
								:class="comparison.essential ? 'included' : 'excluded'" />
						</div>
						<div class="plan_column">
							<sw-icon :name="comparison.enterprise ? 'checkmark' : 'cross'" 
								:class="comparison.enterprise ? 'included' : 'excluded'" />
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- FAQ Section -->
		<div class="faq_section">
			<div class="faq_container">
				<h2 class="faq_title">Perguntas Frequentes</h2>
				
				<div class="faq_grid">
					<div class="faq_item" v-for="faq in faqs" :key="faq.question">
						<h3 class="faq_question">{{ faq.question }}</h3>
						<p class="faq_answer">{{ faq.answer }}</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { ref, computed } from 'vue';
	import { useRouter } from 'vue-router';
	import { notify } from 'sysweb-vue-components/src/global';
	
	import { generateCheckoutSlug } from '@/services/mockApi';
	import { mockPlans, mockFeatureComparisons, mockPlanFaqs } from '@/data/mockData';
	import PlanResources from '@Component/PlanResources.vue';

	const router = useRouter();
	const billingCycle = ref('monthly');

	const plans = ref(mockPlans);
	const featureComparisons = ref(mockFeatureComparisons);
	const faqs = ref(mockPlanFaqs);

	// Estado para recursos customizáveis por plano
	const planResources = ref({
		essential: {
			storage: 10, // GB base
			sharingPages: 10, // páginas de compartilhamento base
			uploadPages: 10, // páginas de upload base
			emailsSent: 100, // e-mails por mês base
			alerts: 20 // alertas por mês base
		},
		enterprise: {
			storage: 100, // GB base
			sharingPages: 20, // páginas de compartilhamento base
			uploadPages: 20, // páginas de upload base
			emailsSent: 500, // e-mails por mês base
			alerts: 100 // alertas por mês base
		}
	});


	// Estado para controlar exibição de features
	const featuresVisibility = ref({});

	// Inicializar estados de visibilidade para cada plano
	plans.value.forEach(plan => {
		featuresVisibility.value[plan.id] = false;
	});


	// Estado para custos extras de recursos por plano
	const planResourcesCosts = ref({
		essential: 0,
		enterprise: 0
	});

	// Computed para features visíveis por plano
	const getVisibleFeatures = (plan) => {
		const isExpanded = featuresVisibility.value[plan.id];
		const maxVisible = 5;
		return isExpanded ? plan.features : plan.features.slice(0, maxVisible);
	};

	// Computed para contar features restantes
	const getRemainingFeaturesCount = (plan) => {
		const maxVisible = 5;
		const remaining = plan.features.length - maxVisible;
		return remaining > 0 ? remaining : 0;
	};

	// Função para alternar visibilidade
	const toggleFeaturesVisibility = (planId) => {
		featuresVisibility.value[planId] = !featuresVisibility.value[planId];
	};

	// Métodos para o componente PlanResources
	const getBaseResources = (planId) => {
		return planId === 'essential' 
			? { storage: 10, sharingPages: 10, uploadPages: 10, emailsSent: 100, alerts: 20 }
			: { storage: 100, sharingPages: 20, uploadPages: 20, emailsSent: 500, alerts: 100 };
	};

	const updatePlanResources = (planId, resources) => {
		planResources.value[planId] = resources;
	};

	const updatePlanExtraCost = (planId, cost) => {
		planResourcesCosts.value[planId] = cost;
	};




	// Calcula preço anual com desconto dinâmico
	const getYearlyPrice = (plan) => {
		const monthlyPrice = plan.price.monthly;
		const yearlyTotal = monthlyPrice * 12;
		const discount = yearlyTotal * (plan.annualDiscount || 0);
		return yearlyTotal - discount;
	};

	// Calcula economia anual
	const getYearlySavings = (plan) => {
		const monthlyPrice = plan.price.monthly;
		const yearlyTotal = monthlyPrice * 12;
		const yearlyPrice = getYearlyPrice(plan);
		return yearlyTotal - yearlyPrice;
	};

	// Obtém porcentagem de desconto do plano
	const getDiscountPercentage = (plan) => {
		return Math.round((plan.annualDiscount || 0) * 100);
	};

	// Obtém maior desconto entre todos os planos
	const getMaxDiscount = () => {
		const maxDiscount = Math.max(...plans.value.map(plan => plan.annualDiscount || 0));
		return Math.round(maxDiscount * 100);
	};

	const getFeatureClass = (feature) => {
		if (!feature.included) return 'disabled';
		if (feature.highlight) return 'highlight';
		if (feature.inDevelopment) return 'development';
		return 'included';
	};


	const upgradeToPlan = (planType, event) => {
		// Completamente prevenir qualquer comportamento de navegador
		if (event) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
		}
		
		// Use setTimeout para garantir que estamos fora do contexto de evento
		setTimeout(() => {
			performNavigation(planType);
		}, 0);
	};

	const performNavigation = async (planType) => {
		const selectedPlan = plans.value.find(plan => plan.id === planType);

		if (!selectedPlan) {
			notify('Plano não encontrado', 'error');
			return;
		}

		try {
			// Navega imediatamente para checkout passando dados via sessionStorage
			const tempSlug = `temp_${selectedPlan.id}_${Date.now()}`;
			
			// Armazena dados temporariamente no sessionStorage
			const checkoutData = {
				planData: {
					id: selectedPlan.id,
					name: selectedPlan.name,
					price: selectedPlan.price,
					description: selectedPlan.description,
					icon: selectedPlan.icon,
					features: selectedPlan.features,
					featured: selectedPlan.featured,
					billing: billingCycle.value,
					originalPrice: selectedPlan.price.monthly
				},
				resources: {
					storage: planResources.value[selectedPlan.id].storage,
					sharingPages: planResources.value[selectedPlan.id].sharingPages,
					uploadPages: planResources.value[selectedPlan.id].uploadPages,
					emailsSent: planResources.value[selectedPlan.id].emailsSent,
					alerts: planResources.value[selectedPlan.id].alerts
				},
				extraCost: planResourcesCosts.value[selectedPlan.id]
			};
			
			sessionStorage.setItem(`checkout_data_${tempSlug}`, JSON.stringify(checkoutData));
			
			await router.push({
				name: 'Checkout',
				params: {
					slug: tempSlug
				}
			});

		} catch (error) {
			console.error('Erro na navegação:', error);
			notify('Erro ao navegar para checkout', 'error');
		}

		// Gera slug real em background (opcional para persistência futura)
		try {
			const planData = {
				id: selectedPlan.id,
				billing: billingCycle.value,
				resources: {
					storage: planResources.value[selectedPlan.id].storage,
					sharingPages: planResources.value[selectedPlan.id].sharingPages,
					uploadPages: planResources.value[selectedPlan.id].uploadPages,
					emailsSent: planResources.value[selectedPlan.id].emailsSent,
					alerts: planResources.value[selectedPlan.id].alerts
				},
				resourcesExtraCost: planResourcesCosts.value[selectedPlan.id]
			};

			generateCheckoutSlug(planData); // Não aguarda resposta
		} catch (error) {
			console.error('Erro ao gerar slug em background:', error);
		}
	};
</script>

<style lang="scss" scoped>
	.planUpgrade {
		// CSS Variables para gradientes e cores comuns
		--gradient-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		--gradient-primary-hover: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
		--gradient-hero: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		--gradient-light: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
		--gradient-surface: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
		
		// Cores principais
		--color-primary: #667eea;
		--color-text: #111827;
		--color-text-muted: #6b7280;
		--color-text-light: #374151;
		
		// Box shadows
		--shadow-card: 0 4px 20px rgba(0, 0, 0, 0.08);
		--shadow-card-hover: 0 20px 40px rgba(0, 0, 0, 0.12);
		--shadow-primary: 0 10px 30px rgba(102, 126, 234, 0.2);
		
		min-height: 100vh;
		background: var(--gradient-surface);
	}

	// Hero Section
	.hero {
		padding: 3rem 2rem;
		text-align: center;
		background: var(--gradient-hero);
		color: white;
		position: relative;
		overflow: hidden;

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
		}
	}

	.hero_container {
		max-width: 800px;
		margin: 0 auto;
		position: relative;
		z-index: 1;
	}

	.hero_badge {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 1rem;
		background: rgba(255, 255, 255, 0.2);
		border: 1px solid rgba(255, 255, 255, 0.3);
		border-radius: 50px;
		font-size: 0.875rem;
		font-weight: 600;
		margin-bottom: 1.5rem;
		backdrop-filter: blur(10px);
		
		.badge_icon {
			animation: sparkle 2s infinite;
		}
	}

	@keyframes sparkle {
		0%, 100% { transform: scale(1); }
		50% { transform: scale(1.2); }
	}

	.hero_title {
		font-size: 2.5rem;
		font-weight: 800;
		margin: 0 0 0.75rem 0;
		line-height: 1.2;

		.highlight {
			background: linear-gradient(45deg, #fbbf24, #f59e0b);
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			background-clip: text;
		}
	}

	.hero_subtitle {
		font-size: 1.125rem;
		margin: 0 0 1.5rem 0;
		opacity: 0.9;
		max-width: 500px;
		margin-left: auto;
		margin-right: auto;
	}

	// Billing Toggle
	.billing_toggle {
		display: inline-flex;
		background: rgba(255, 255, 255, 0.2);
		border-radius: 50px;
		padding: 0.25rem;
		gap: 0.25rem;
		backdrop-filter: blur(10px);
		border: 1px solid rgba(255, 255, 255, 0.3);
	}

	.toggle_btn {
		padding: 0.75rem 1.5rem;
		border: none;
		border-radius: 50px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;
		background: transparent;
		color: rgba(255, 255, 255, 0.8);
		display: flex;
		align-items: center;
		gap: 0.5rem;

		&.active {
			background: white;
			color: #667eea;
			box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
		}

		&:not(.active):hover {
			background: rgba(255, 255, 255, 0.1);
		}
	}

	.discount_badge {
		background: #10b981;
		color: white;
		padding: 0.25rem 0.5rem;
		border-radius: 12px;
		font-size: 0.75rem;
		font-weight: 700;
	}


	// Plans Section
	.plans_section {
		padding: 4rem 2rem;
	}

	.plans_container {
		max-width: 1200px;
		margin: 0 auto;
	}

	.plans_grid {
		display: flex;
		flex-wrap: wrap;
		gap: 2rem;
		max-width: 900px;
		margin: 0 auto;
		align-items: stretch;
	}

	.plan_card {
		background: white;
		border: 2px solid #e5e7eb;
		border-radius: 1.5rem;
		padding: 2rem;
		position: relative;
		transition: all 0.3s ease;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
		flex: 1;
		min-width: 350px;
		max-width: 450px;
		align-self: flex-start;

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
		}

		&.featured {
			border-color: var(--color-primary);
			box-shadow: var(--shadow-primary);

			&:hover {
				transform: translateY(-5px);
				box-shadow: 0 25px 50px rgba(102, 126, 234, 0.3);
			}
		}
	}

	.popular_badge {
		position: absolute;
		top: -12px;
		left: 50%;
		transform: translateX(-50%);
		background: var(--gradient-primary);
		color: white;
		padding: 0.5rem 1rem;
		border-radius: 20px;
		font-size: 0.875rem;
		font-weight: 600;
		display: flex;
		align-items: center;
		gap: 0.25rem;
		box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
	}

	// New Plan Layout
	.plan_content {
		display: flex;
		flex-direction: column;
		height: 100%;
	}

	.plan_top {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: 1.5rem;
		gap: 1rem;
	}

	.plan_info {
		display: flex;
		align-items: flex-start;
		gap: 0.75rem;
		flex: 1;
	}

	.plan_icon_small {
		width: 2.5rem;
		height: 2.5rem;
		border-radius: 0.75rem;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1rem;
		color: white;
		flex-shrink: 0;

		&.essential {
			background: linear-gradient(135deg, #6b73ff 0%, #000dff 100%);
		}

		&.enterprise {
			background: var(--gradient-primary);
		}
	}

	.plan_details {
		flex: 1;
		min-width: 0;
	}

	.plan_name {
		font-size: 1.25rem;
		font-weight: 700;
		color: var(--color-text);
		margin: 0 0 0.25rem 0;
		line-height: 1.3;
	}

	.plan_description {
		color: var(--color-text-muted);
		margin: 0;
		font-size: 0.875rem;
		line-height: 1.4;
	}

	.plan_price_compact {
		text-align: right;
		flex-shrink: 0;
	}

	.price_value {
		display: flex;
		align-items: baseline;
		justify-content: flex-end;
		gap: 0.25rem;
		margin-bottom: 0.25rem;
	}

	.currency_small {
		font-size: 1rem;
		font-weight: 600;
		color: var(--color-text-muted);
	}

	.amount_large {
		font-size: 2rem;
		font-weight: 800;
		color: var(--color-text);
		line-height: 1;
	}

	.price_period_small {
		font-size: 0.875rem;
		color: var(--color-text-muted);
		font-weight: 500;
	}


	.price_extras {
		font-size: 0.75rem;
		color: #f59e0b;
		font-weight: 600;
		margin-top: 0.25rem;
		text-align: right;
	}

	.price_savings {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		font-size: 0.75rem;
		color: #059669;
		font-weight: 600;
		background: #dcfdf7;
		padding: 0.25rem 0.5rem;
		border-radius: 0.375rem;
		margin-top: 0.5rem;
		white-space: nowrap;

		.sw-icon {
			font-size: 0.875rem;
		}
	}



	.plan_features_detailed {
		margin-bottom: 2rem;
		flex: 1;
	}

	.features_heading {
		font-size: 1rem;
		font-weight: 600;
		color: #374151;
		margin: 0 0 1rem 0;
		padding-bottom: 0.5rem;
		border-bottom: 2px solid #f3f4f6;
	}

	.features_list_detailed {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.feature_item_detailed {
		display: flex;
		align-items: flex-start;
		gap: 0.75rem;
		padding: 0.75rem;
		border-radius: 0.5rem;
		transition: all 0.2s ease;

		&:hover {
			background: #f9fafb;
		}

		&.disabled {
			opacity: 0.5;
			
			.feature_text_detailed {
				text-decoration: line-through;
			}
		}

		&.highlight {
			background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05));
			border: 1px solid rgba(102, 126, 234, 0.1);
			
			.feature_text_detailed {
				color: #667eea;
				font-weight: 600;
			}

			.feature_icon_detailed {
				background: #667eea;
				color: white;
			}
		}

		&.development {
			background: linear-gradient(135deg, rgba(245, 158, 11, 0.03), rgba(251, 191, 36, 0.03));
			border: 1px solid rgba(245, 158, 11, 0.1);
			opacity: 0.8;
			
			.feature_text_detailed {
				color: #92400e;
			}

			.feature_icon_detailed {
				background: #f59e0b;
				color: white;
				opacity: 0.7;
			}

			&:hover {
				background: linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(251, 191, 36, 0.05));
			}
		}
	}

	.feature_icon_detailed {
		width: 2rem;
		height: 2rem;
		background: #10b981;
		color: white;
		border-radius: 0.5rem;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 0.875rem;
		flex-shrink: 0;
		box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
	}

	.feature_content_detailed {
		flex: 1;
		min-width: 0;
	}

	.feature_text_detailed {
		font-size: 0.9375rem;
		font-weight: 500;
		color: var(--color-text);
		display: block;
		margin-bottom: 0.25rem;
		line-height: 1.3;
	}

	.feature_description {
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		line-height: 1.4;
		margin: 0;
	}

	.development_badge {
		display: inline-flex;
		align-items: center;
		gap: 0.25rem;
		margin-left: 0.5rem;
		padding: 0.125rem 0.375rem;
		background: rgba(245, 158, 11, 0.1);
		color: #92400e;
		border-radius: 0.25rem;
		font-size: 0.6875rem;
		font-weight: 500;
		border: 1px solid rgba(245, 158, 11, 0.2);
		vertical-align: middle;
	}

	.features_toggle_container {
		margin-top: 1rem;
		padding-top: 0.75rem;
		border-top: 1px solid #f3f4f6;
		text-align: center;
	}

	.features_toggle_btn {
		background: transparent;
		border: 1.5px solid #e5e7eb;
		border-radius: 0.5rem;
		padding: 0.5rem 0.875rem;
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--color-text-muted);
		cursor: pointer;
		transition: all 0.2s ease;
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;

		&:hover {
			background: #f9fafb;
			border-color: #d1d5db;
			color: #374151;

			.toggle_icon {
				transform: scale(1.1);
			}
		}

		&:active {
			transform: translateY(1px);
		}

		.toggle_icon {
			font-size: 0.875rem;
			transition: all 0.2s ease;
		}
	}

	.plan_button_modern {
		width: 100%;
		padding: 0.875rem 1rem;
		border: 2px solid #e5e7eb;
		border-radius: 0.75rem;
		font-size: 0.9375rem;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;
		background: white;
		color: #374151;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		margin-top: auto;

		.button_arrow {
			font-size: 0.875rem;
			transition: transform 0.3s ease;
		}

		&:hover {
			background: #f9fafb;
			border-color: #d1d5db;
			
			.button_arrow {
				transform: translateX(2px);
			}
		}

		&.primary {
			background: var(--gradient-primary);
			border-color: #667eea;
			color: white;

			&:hover {
				transform: translateY(-1px);
				box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
				
				.button_arrow {
					transform: translateX(3px);
				}
			}
		}
	}

	// Features Comparison
	.features_section {
		padding: 4rem 2rem;
		background: #f8fafc;
	}

	.features_container {
		max-width: 800px;
		margin: 0 auto;
	}

	.features_title {
		text-align: center;
		font-size: 2.5rem;
		font-weight: 700;
		color: var(--color-text);
		margin: 0 0 3rem 0;
	}

	.comparison_table {
		background: white;
		border-radius: 1rem;
		overflow: hidden;
		box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
	}

	.comparison_header {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr;
		background: #111827;
		color: white;
		font-weight: 600;
		
		.feature_name {
			padding: 1.5rem;
		}

		.plan_column {
			padding: 1.5rem;
			text-align: center;
			border-left: 1px solid rgba(255, 255, 255, 0.1);

			&.featured {
				background: #667eea;
			}
		}
	}

	.comparison_row {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr;
		border-bottom: 1px solid #e5e7eb;

		&:last-child {
			border-bottom: none;
		}

		.feature_name {
			padding: 1rem 1.5rem;
			font-weight: 500;
		}

		.plan_column {
			padding: 1rem;
			text-align: center;
			border-left: 1px solid #e5e7eb;
			display: flex;
			align-items: center;
			justify-content: center;

			.included {
				color: #10b981;
			}

			.excluded {
				color: #ef4444;
			}
		}
	}

	// FAQ Section
	.faq_section {
		padding: 4rem 2rem;
	}

	.faq_container {
		max-width: 800px;
		margin: 0 auto;
	}

	.faq_title {
		text-align: center;
		font-size: 2.5rem;
		font-weight: 700;
		color: var(--color-text);
		margin: 0 0 3rem 0;
	}

	.faq_grid {
		display: grid;
		gap: 2rem;
	}

	.faq_item {
		background: white;
		padding: 2rem;
		border-radius: 1rem;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
		border: 2px solid #f3f4f6;
		transition: all 0.3s ease;

		&:hover {
			border-color: #667eea;
			transform: translateY(-2px);
			box-shadow: 0 8px 30px rgba(102, 126, 234, 0.15);
		}
	}

	.faq_question {
		font-size: 1.25rem;
		font-weight: 600;
		color: var(--color-text);
		margin: 0 0 1rem 0;
	}

	.faq_answer {
		color: var(--color-text-muted);
		line-height: 1.6;
		margin: 0;
	}

	// Responsive Design
	@media (max-width: 768px) {
		.hero_title {
			font-size: 2rem;
		}


		.plans_grid {
			flex-direction: column;
			align-items: center;
		}

		.plan_card {
			max-width: 100%;
			min-width: 300px;
		}

		.comparison_header,
		.comparison_row {
			grid-template-columns: 1.5fr 1fr 1fr;
			font-size: 0.875rem;
		}

		.features_title,
		.faq_title {
			font-size: 2rem;
		}
	}

	@media (max-width: 480px) {
		.hero,
		.plans_section,
		.features_section,
		.faq_section {
			padding-left: 1rem;
			padding-right: 1rem;
		}

		.hero_title {
			font-size: 1.75rem;
		}

		.amount_large {
			font-size: 2.5rem;
		}

		.plan_card {
			padding: 1.5rem;
		}
	}
</style>